import { Code, Text } from "@radix-ui/themes";
import { useQueryErrorResetBoundary } from "@tanstack/react-query";
import { ErrorComponent } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { EnterpriseMfaStepPending } from "./EnterpriseMfaStepPending";

import { ErrorPage } from "../../../components/ErrorPage";
import { isEnterpriseMfaStepParkedError } from "../../../utils/enterpriseMfaStep";
import { errorMessage } from "../../../utils/http";

export const TicketsError: typeof ErrorComponent = ({
  error,
}: {
  error: Error | Response;
}) => {
  const { t } = useTranslation("ui");
  const queryErrorResetBoundary = useQueryErrorResetBoundary();
  const [message, setMessage] = useState<string>();

  // The folder share-link read parks an MFA step and raises this instead of
  // returning a listing (`services/projects.ts`). The ticket route's error
  // component names the step; this route must too, or a folder share link into
  // a flagged enterprise reads as a failed folder load. Read once, here, so the
  // render below and the effect's own skip agree on the same verdict.
  const mfaStepParked = isEnterpriseMfaStepParkedError(error);

  useEffect(() => {
    // No message is resolved for a parked step: the pending page replaces the
    // ErrorPage entirely, and `errorMessage` would otherwise print the marker's
    // own sentence behind the open TOTP dialog.
    if (mfaStepParked) {
      return;
    }
    if (error instanceof Response && error.status === 401) {
      setMessage("You are not authorized to access this folder.");
    } else {
      errorMessage(error).then(setMessage);
    }
  }, [error, mfaStepParked]);

  useEffect(() => {
    queryErrorResetBoundary.reset();
  }, [queryErrorResetBoundary]);

  if (mfaStepParked) {
    return <EnterpriseMfaStepPending />;
  }

  return (
    <ErrorPage
      title={t("tickets.ticketsError.error")}
      description={t("tickets.ticketsError.unableRetrieveTicketsBecauseError")}
      variant="error"
    >
      <Code
        color="red"
        data-testid="tickets-error-message"
        className="text-red-600"
      >
        {message}
      </Code>
      <Text
        color="red"
        data-testid="tickets-error-help-text"
        className="text-red-600"
      >
        {t("tickets.ticketsError.pleaseTryAgainShortlyContact")}
      </Text>
    </ErrorPage>
  );
};
